import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  TrendingUp,
  MapPin,
  PoundSterling,
  BarChart3,
  Phone,
  Building2,
  Users,
  Home,
  Award,
  HandCoins,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import api from "../lib/api";
import logger from "../lib/logger";

const ICONS = {
  MapPin,
  PoundSterling,
  BarChart3,
  TrendingUp,
  Building2,
  Users,
  Home,
  Award,
  HandCoins,
};

const DEFAULTS = {
  hero_eyebrow: "Our Services",
  title: "Property Investment",
  description:
    "Expert guidance on property investment opportunities across West Yorkshire. Leverage our local knowledge to make informed investment decisions.",
  hero_cta_text: "Discuss Investment Options",
  services_section_title: "Investment Services",
  services_section_subtitle: "We help investors at all levels make informed decisions about West Yorkshire property.",
  services_items: [
    { icon: "MapPin", title: "Area Analysis", description: "In-depth analysis of West Yorkshire locations, rental demand, and growth potential." },
    { icon: "PoundSterling", title: "Yield Assessment", description: "Accurate rental valuations and realistic yield projections based on local market knowledge." },
    { icon: "BarChart3", title: "Portfolio Strategy", description: "Guidance on building and optimising your property portfolio for long-term returns." },
    { icon: "TrendingUp", title: "Market Insights", description: "Regular updates on West Yorkshire market trends, opportunities, and developments." },
  ],
  areas_section_title: "Investment Areas",
  areas_section_subtitle: "We specialise in West Yorkshire's key investment locations.",
  areas_items: [
    { name: "Leeds", description: "West Yorkshire's largest city with strong rental demand from students and professionals. Growing tech sector and excellent transport links.", highlights: ["High rental yields", "Strong capital growth", "Major regeneration projects"] },
    { name: "Bradford", description: "Affordable entry point with improving infrastructure. Growing interest from investors due to regeneration plans and proximity to Leeds.", highlights: ["Lower purchase prices", "Good rental yields", "Regeneration opportunities"] },
    { name: "Batley", description: "Our home base with strong local demand. Family-friendly areas with good schools and transport connections.", highlights: ["Strong family rental market", "Local expertise", "Competitive pricing"] },
    { name: "Dewsbury", description: "Affordable market with steady rental demand. Good transport links and ongoing investment in the town centre.", highlights: ["Entry-level investment", "Steady demand", "Transport improvements"] },
  ],
  cta_title: "Start Your Investment Journey",
  cta_description: "Whether you're a first-time investor or expanding your portfolio, we can help you navigate the West Yorkshire market.",
  cta_button_text: "Book a Consultation",
  cta_phone: "01924 520163",
};

const InvestmentsPage = () => {
  const [content, setContent] = useState(DEFAULTS);

  useEffect(() => {
    let mounted = true;
    api
      .getContentSection("services_investments")
      .then((res) => {
        if (mounted && res?.content) {
          setContent({ ...DEFAULTS, ...res.content });
        }
      })
      .catch((err) => {
        logger.warn("Failed to load services_investments:", err?.message);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const renderIcon = (name, className) => {
    const Icon = ICONS[name] || TrendingUp;
    return <Icon className={className} />;
  };

  const ctaPhoneDigits = (content.cta_phone || "").replace(/\s/g, "");

  return (
    <div data-testid="investments-page">
      {/* Hero */}
      <section className="bg-navy text-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-gold font-medium text-sm tracking-wider uppercase mb-4 block">
              {content.hero_eyebrow}
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-semibold leading-tight mb-6">
              {content.title}
            </h1>
            <p className="text-xl text-white/80 mb-8 leading-relaxed">{content.description}</p>
            <Button asChild size="lg" className="bg-gold hover:bg-gold-light text-navy font-semibold" data-testid="investments-cta">
              <Link to="/contact">
                {content.hero_cta_text}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-navy mb-4">
              {content.services_section_title}
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">{content.services_section_subtitle}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8" data-testid="investments-services-grid">
            {(content.services_items || []).map((service, index) => (
              <Card key={index} className="border-slate-200 hover:shadow-lg transition-shadow">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 rounded-lg bg-navy/5 flex items-center justify-center mx-auto mb-6">
                    {renderIcon(service.icon, "w-7 h-7 text-navy")}
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-navy mb-3">{service.title}</h3>
                  <p className="text-slate-600 text-sm">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Areas */}
      <section className="py-20 md:py-28 bg-off-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-navy mb-4">
              {content.areas_section_title}
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">{content.areas_section_subtitle}</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8" data-testid="investments-areas-grid">
            {(content.areas_items || []).map((area, index) => (
              <Card key={index} className="border-slate-200 hover:shadow-lg transition-shadow">
                <CardContent className="p-8">
                  <h3 className="font-heading text-2xl font-semibold text-navy mb-4">{area.name}</h3>
                  <p className="text-slate-600 mb-6">{area.description}</p>
                  <div className="space-y-2">
                    {(area.highlights || []).map((highlight, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-gold" />
                        <span className="text-slate-700 text-sm">{highlight}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-navy text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold mb-6" data-testid="investments-cta-title">{content.cta_title}</h2>
          <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">{content.cta_description}</p>
          <Button asChild size="lg" className="bg-gold hover:bg-gold-light text-navy font-semibold">
            <Link to="/contact">
              {content.cta_button_text}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </Button>
          <div className="mt-6 flex items-center justify-center gap-2 text-white/70">
            <Phone className="w-5 h-5" />
            <span>Or call </span>
            <a href={`tel:${ctaPhoneDigits}`} className="text-gold hover:text-gold-light font-medium">
              {content.cta_phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default InvestmentsPage;
